import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// Domain Name services with their cached Synergy Wholesale registry data. Open to all staff; the Synergy account
// balance at the top is admin only. Registry data is refreshed nightly (and by Registry Sync on each domain).

interface Row {
  id: string;
  domain: string;
  service_id: string;
  account_id: string;
  account_name: string | null;
  account_number: string | null;
  service_status: string;
  registry_status: string | null;
  expiry_at: string | null;
  days_left: number | null;
  auto_renew: boolean | null;
  id_protect: boolean | null;
  synced: boolean;
  last_synced_at: string | null;
}

interface Report {
  rows: Row[];
  tiles: { total: number; expiring_30: number; expired: number; auto_renew_off: number; not_synced: number };
  shown: number;
}

interface Balance {
  configured: boolean;
  balance: number | null;
  error: string | null;
  fetched_at?: string;
}

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'all', label: 'All statuses' },
];
const EXPIRY_OPTIONS = [
  { value: 'all', label: 'Any expiry' },
  { value: 'expired', label: 'Expired' },
  { value: '30', label: 'Expires in 30 days' },
  { value: '60', label: 'Expires in 60 days' },
  { value: '90', label: 'Expires in 90 days' },
];
const AUTO_RENEW_OPTIONS = [
  { value: 'all', label: 'Auto-renew: any' },
  { value: 'on', label: 'Auto-renew on' },
  { value: 'off', label: 'Auto-renew off' },
];

const money = (n: number) => n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
const dateFmt = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const yesNo = (v: boolean | null) => (v === null ? '—' : v ? 'On' : 'Off');

// Spreadsheet apps run text starting with = + - @ as a formula; neutralise user-supplied text
const csvText = (v: string | null | undefined) => {
  const s = (v ?? '').toString();
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
};

function buildCsv(rows: Row[]): string {
  const header = ['Domain', 'Account', 'Account number', 'Service status', 'Registry status', 'Expiry', 'Days left', 'Auto-renew', 'ID protection', 'Last synced'];
  const lines = [header.map((h) => `"${h}"`).join(',')];
  for (const r of rows) {
    lines.push([
      csvText(r.domain), csvText(r.account_name), csvText(r.account_number), csvText(r.service_status), csvText(r.registry_status),
      csvText(r.expiry_at ? r.expiry_at.slice(0, 10) : ''), csvText(r.days_left === null ? '' : String(r.days_left)),
      csvText(yesNo(r.auto_renew)), csvText(yesNo(r.id_protect)), csvText(r.last_synced_at ? r.last_synced_at.slice(0, 10) : 'Not synced'),
    ].join(','));
  }
  return lines.join('\r\n');
}

function downloadCsv(filename: string, content: string) {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const Tile: React.FC<{ label: string; value: number; onClick?: () => void; active?: boolean; warn?: boolean }> = ({ label, value, onClick, active, warn }) => (
  <Paper
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 2, flex: '1 1 130px', minWidth: 130, cursor: onClick ? 'pointer' : 'default',
      borderColor: active ? 'primary.main' : undefined,
      '&:hover': onClick ? { borderColor: 'primary.main' } : undefined,
    }}
  >
    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</Typography>
    <Typography variant="h5" sx={{ fontWeight: 700, color: warn && value > 0 ? 'warning.main' : undefined }}>{value.toLocaleString()}</Typography>
  </Paper>
);

const DomainNamesReportPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState('active');
  const [expiry, setExpiry] = useState('all');
  const [autoRenew, setAutoRenew] = useState('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [balance, setBalance] = useState<Balance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReport(await apiService.getDomainNamesReport({ status, expiry, auto_renew: autoRenew, q }));
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not load the report.');
    } finally {
      setLoading(false);
    }
  }, [status, expiry, autoRenew, q]);

  useEffect(() => { load(); }, [load]);

  const loadBalance = useCallback(async (refresh = false) => {
    if (!isAdmin) return;
    setBalanceLoading(true);
    try {
      setBalance(await apiService.getSynergyBalance(refresh));
    } catch {
      setBalance({ configured: true, balance: null, error: 'Could not load the Synergy balance.' });
    } finally {
      setBalanceLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { loadBalance(); }, [loadBalance]);

  const filtersActive = expiry !== 'all' || autoRenew !== 'all' || !!search;
  const tiles = report?.tiles;
  const exportCsv = () => {
    if (!report) return;
    downloadCsv(`domain-names-${status}${filtersActive ? '-filtered' : ''}-${new Date().toISOString().slice(0, 10)}.csv`, buildCsv(report.rows));
  };

  const expiryChip = (r: Row) => {
    if (r.days_left === null) return null;
    if (r.days_left < 0) return <Chip size="small" color="error" label={`Expired ${-r.days_left}d ago`} sx={{ ml: 1 }} />;
    if (r.days_left <= 30) return <Chip size="small" color="warning" label={`${r.days_left}d left`} sx={{ ml: 1 }} />;
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>Domain Names</Typography>
          <Typography variant="body2" color="text.secondary">
            Domain Name services with their Synergy Wholesale registry details. Registry data is refreshed nightly.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!report || loading}>Export CSV</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => { load(); loadBalance(true); }} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      {isAdmin && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Synergy Wholesale account balance
            </Typography>
            {balanceLoading && !balance ? (
              <Box sx={{ py: 0.5 }}><CircularProgress size={20} /></Box>
            ) : balance?.balance != null ? (
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{money(balance.balance)}</Typography>
            ) : (
              <Typography variant="body1" color="text.secondary">{balance?.error || 'Balance unavailable.'}</Typography>
            )}
            {balance?.fetched_at && (
              <Typography variant="caption" color="text.secondary">
                As at {new Date(balance.fetched_at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })}
              </Typography>
            )}
          </Box>
          <Button size="small" onClick={() => loadBalance(true)} disabled={balanceLoading}>Refresh balance</Button>
        </Paper>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Search domain or account" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 140 }}>
          {STATUS_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Expiry" value={expiry} onChange={(e) => setExpiry(e.target.value)} sx={{ minWidth: 190 }}>
          {EXPIRY_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Auto-renew" value={autoRenew} onChange={(e) => setAutoRenew(e.target.value)} sx={{ minWidth: 170 }}>
          {AUTO_RENEW_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        {filtersActive && <Button size="small" onClick={() => { setExpiry('all'); setAutoRenew('all'); setSearch(''); }}>Clear filters</Button>}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {tiles && (
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Tile label="Domains" value={tiles.total} onClick={() => { setExpiry('all'); setAutoRenew('all'); }} active={expiry === 'all' && autoRenew === 'all'} />
          <Tile label="Expiring in 30 days" value={tiles.expiring_30} warn onClick={() => setExpiry(expiry === '30' ? 'all' : '30')} active={expiry === '30'} />
          <Tile label="Expired" value={tiles.expired} warn onClick={() => setExpiry(expiry === 'expired' ? 'all' : 'expired')} active={expiry === 'expired'} />
          <Tile label="Auto-renew off" value={tiles.auto_renew_off} onClick={() => setAutoRenew(autoRenew === 'off' ? 'all' : 'off')} active={autoRenew === 'off'} />
          <Tile label="Not synced" value={tiles.not_synced} warn />
        </Box>
      )}

      <Paper variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Domain</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Expiry</TableCell>
                <TableCell>Registry status</TableCell>
                <TableCell>Auto-renew</TableCell>
                <TableCell>ID protection</TableCell>
                <TableCell>Last synced</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && !report && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6 }}><CircularProgress size={28} /></TableCell></TableRow>
              )}
              {report && report.rows.length === 0 && !loading && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: 'text.secondary' }}>No domains match these filters.</TableCell></TableRow>
              )}
              {report?.rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.domain}</TableCell>
                  <TableCell>
                    <Box component={RouterLink} to={`/accounts/${r.account_id}`} sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {r.account_name || '—'}
                    </Box>
                    {r.account_number && <Typography variant="caption" color="text.secondary" display="block">{r.account_number}</Typography>}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFmt(r.expiry_at)}{expiryChip(r)}</TableCell>
                  <TableCell>{r.registry_status || '—'}</TableCell>
                  <TableCell>{yesNo(r.auto_renew)}</TableCell>
                  <TableCell>{yesNo(r.id_protect)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.synced ? dateFmt(r.last_synced_at) : <Typography variant="body2" color="warning.main">Not synced</Typography>}</TableCell>
                  <TableCell>
                    <Chip size="small" label={r.service_status} color={r.service_status === 'active' ? 'success' : 'default'} variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default DomainNamesReportPage;
